import { useTranslation } from '@thoughtspot/i18n';
import { collectEvent, UserAction, userActionTypeEnum, } from '@thoughtspot/metrics';
import { Icon, IconColor, IconID, IconSize, } from '@thoughtspot/radiant-react/widgets/icon';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import cx from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import ReactDOM from 'react-dom';
import { getAllSpotlightNavItems } from './items';
import styles from './spotlight.module.scss';
import { filterSpotlightItems, SpotlightItemType, } from './spotlight.types';
import { buildObjectSearchItem, buildSpotterPath, buildSpotterSuggestionItem, } from './spotlight-completions.util';
import { CREATE_ACTION_IDS, getOrderedSlashMenuItems, getQuickLinks, getSlashMenuItems, RECENT_CONFIG, TYPE_LABEL_CONFIG, } from './spotlight-config';
import { EurekaObjectType, EurekaQueryCompletionType, useSpotlightEurekaCompletions, } from './use-spotlight-completions';
import { useSpotlightShortcut } from './use-spotlight-shortcut';
const ONBOARDING_HINT_KEYS = [
    'spotlight.onboarding.hint1',
    'spotlight.onboarding.hint2',
    'spotlight.onboarding.hint3',
    'spotlight.onboarding.hint4',
];
const EUREKA_OBJECT_PATH_MAP = {
    [EurekaObjectType.ANSWER]: 'saved-answer',
    [EurekaObjectType.PINBOARD]: 'pinboard',
    [EurekaObjectType.VISUALIZATION]: 'pinboard',
    [EurekaObjectType.WORKSHEET]: 'data/tables',
};
const EUREKA_OBJECT_ICON_MAP = {
    [EurekaObjectType.ANSWER]: IconID.ANSWER,
    [EurekaObjectType.PINBOARD]: IconID.PINBOARD_NEW,
    [EurekaObjectType.VISUALIZATION]: IconID.ANSWER,
    [EurekaObjectType.WORKSHEET]: IconID.SAVE_WORKSHEET,
};
export const Spotlight = ({ onItemSelect, items: customItems, enabled = true, navService, enableEurekaSearch = false, eurekaSearchPath = '/eureka', filterItems: externalFilterItems, filterItemsLoading = false, onFilterSelect, showAdminItem = true, showSpotterItem = true, spotterPath = '/conv-assist', enableSpotterAutoMode = false, isPathEnabled, currentPath = '', userId, onOnboardingTabClose, orgId, }) => {
    const { t } = useTranslation();
    const tStr = t;
    const recentItemsKey = userId
        ? `spotlight-recent-items-${userId}${orgId != null ? `-${orgId}` : ''}`
        : 'spotlight-recent-items';
    const onboardingLastShownKey = userId
        ? `spotlight-onboarding-last-shown-${userId}`
        : 'spotlight-onboarding-last-shown';
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(0);
    const [activeFilter, setActiveFilter] = useState(null);
    const [onboardingTabIndex, setOnboardingTabIndex] = useState(0);
    const [onboardingTabVisible, setOnboardingTabVisible] = useState(true);
    const [recentItems, setRecentItems] = useState(() => {
        try {
            const stored = localStorage.getItem(recentItemsKey);
            if (!stored)
                return [];
            const parsed = JSON.parse(stored);
            const seen = new Set();
            return parsed.filter(item => !seen.has(item.id) && !!seen.add(item.id));
        }
        catch {
            return [];
        }
    });
    const visibleOnboardingHintKeys = useMemo(() => showSpotterItem
        ? ONBOARDING_HINT_KEYS
        : ONBOARDING_HINT_KEYS.filter(k => k !== 'spotlight.onboarding.hint4'), [showSpotterItem]);
    const searchInputRef = useRef(null);
    const listRef = useRef(null);
    const containerRef = useRef(null);
    const isKeyboardNavigating = useRef(false);
    const isClosingRef = useRef(false);
    const openedAtRef = useRef(0);
    const openedMethodRef = useRef('click');
    const showResultsTimerRef = useRef(null);
    const onboardingTabIndexRef = useRef(0);
    useEffect(() => {
        try {
            const stored = localStorage.getItem(recentItemsKey);
            if (!stored) {
                setRecentItems([]);
                return;
            }
            const parsed = JSON.parse(stored);
            const seen = new Set();
            setRecentItems(parsed.filter(item => !seen.has(item.id) && !!seen.add(item.id)));
        }
        catch {
            setRecentItems([]);
        }
    }, [recentItemsKey]);
    useEffect(() => {
        try {
            const serializable = recentItems.map(({ labelNode: _, ...rest }) => rest);
            localStorage.setItem(recentItemsKey, JSON.stringify(serializable));
        }
        catch {
        }
    }, [recentItems]);
    const isSlashMode = searchQuery.startsWith('/') || searchQuery.startsWith('@');
    const isSearching = !!searchQuery.trim() && !isSlashMode;
    const slashFilterQuery = isSlashMode
        ? searchQuery.slice(1).toLowerCase()
        : '';
    const slashItems = useMemo(() => {
        if (!isSlashMode)
            return [];
        const baseItems = getSlashMenuItems(tStr).filter(item => (showAdminItem || item.id !== 'slash-admin') &&
            (showSpotterItem || item.id !== 'slash-spotter'));
        const orderedItems = getOrderedSlashMenuItems(baseItems, currentPath);
        if (!slashFilterQuery)
            return orderedItems;
        return orderedItems.filter(item => item.label.toLowerCase().startsWith(slashFilterQuery));
    }, [
        isSlashMode,
        slashFilterQuery,
        showAdminItem,
        showSpotterItem,
        tStr,
        currentPath,
    ]);
    useEffect(() => {
        if (isSlashMode)
            setFocusedIndex(0);
    }, [isSlashMode]);
    const isFilterMode = activeFilter !== null && !isSlashMode;
    const quickLinks = useMemo(() => getQuickLinks(tStr), [tStr]);
    const customItemsById = useMemo(() => {
        const map = new Map();
        (customItems ?? []).forEach(item => map.set(item.id, item));
        return map;
    }, [customItems]);
    const hydratedRecentItems = recentItems.map(item => {
        if (item.labelKey) {
            return { ...item, label: tStr(item.labelKey) };
        }
        const live = customItemsById.get(item.id);
        return live ? { ...item, label: live.label } : item;
    });
    const navigateFilterItems = useMemo(() => getAllSpotlightNavItems(isPathEnabled, tStr), [isPathEnabled, tStr]);
    const filterScopedItems = useMemo(() => {
        if (!isFilterMode || !activeFilter)
            return [];
        const filterId = activeFilter.id;
        const fromExternal = () => {
            const items = externalFilterItems ?? [];
            return searchQuery.trim()
                ? filterSpotlightItems(items, searchQuery)
                : items;
        };
        if (filterId === 'slash-create' ||
            filterId.includes('collection') ||
            filterId.includes('model') ||
            filterId.includes('table') ||
            filterId.includes('answer') ||
            filterId.includes('liveboard') ||
            filterId.includes('pinboard'))
            return fromExternal();
        if (filterId.includes('admin')) {
            const adminItems = (customItems ?? []).filter(item => item.type === SpotlightItemType.ADMIN);
            return searchQuery.trim()
                ? filterSpotlightItems(adminItems, searchQuery)
                : adminItems;
        }
        if (filterId === 'slash-spotter') {
            const trimmed = searchQuery.trim();
            const spotterRecentItems = recentItems.filter(item => item.type === SpotlightItemType.SPOTTER_SUGGESTION);
            if (!trimmed)
                return spotterRecentItems;
            if (!spotterPath)
                return [];
            return [
                {
                    id: `spotter-filter-suggestion-${trimmed}`,
                    label: `Open in Spotter for ${trimmed}`,
                    labelNode: (React.createElement(React.Fragment, null,
                        "Open in Spotter for ",
                        React.createElement("strong", null, trimmed))),
                    icon: IconID.CHART_SPOTIQ_ANOMALY_LESS,
                    type: SpotlightItemType.SPOTTER_SUGGESTION,
                    path: buildSpotterPath(spotterPath, trimmed, enableSpotterAutoMode),
                },
            ];
        }
        if (filterId === 'slash-navigate') {
            const adminItems = (customItems ?? []).filter(item => item.type === SpotlightItemType.ADMIN);
            const allNavigateItems = [...navigateFilterItems, ...adminItems];
            return searchQuery.trim()
                ? filterSpotlightItems(allNavigateItems, searchQuery)
                : allNavigateItems;
        }
        if (filterId === 'slash-help') {
            const helpItems = quickLinks.filter(item => item.keywords?.some(k => TYPE_LABEL_CONFIG.help.keywords.includes(k)));
            if (!searchQuery.trim())
                return helpItems;
            return filterSpotlightItems(helpItems, searchQuery);
        }
        return [];
    }, [
        isFilterMode,
        activeFilter,
        externalFilterItems,
        customItems,
        searchQuery,
        recentItems,
        quickLinks,
        navigateFilterItems,
    ]);
    const { objectCompletions, queryCompletions, loading: eurekaLoading, } = useSpotlightEurekaCompletions(searchQuery, enableEurekaSearch &&
        isOpen &&
        isSearching &&
        !isSlashMode &&
        (!isFilterMode ||
            !!(activeFilter?.id.includes('liveboard') ||
                activeFilter?.id.includes('pinboard') ||
                activeFilter?.id.includes('answer') ||
                activeFilter?.id.includes('model'))));
    const eurekaObjectItems = useMemo(() => {
        const isLiveboardFilter = !!(activeFilter?.id.includes('liveboard') ||
            activeFilter?.id.includes('pinboard'));
        const isAnswerFilter = !!activeFilter?.id.includes('answer');
        const isModelFilter = !!activeFilter?.id.includes('model');
        if (!isSearching ||
            (isFilterMode &&
                !isLiveboardFilter &&
                !isAnswerFilter &&
                !isModelFilter))
            return [];
        return objectCompletions.map(obj => {
            const urlSegment = EUREKA_OBJECT_PATH_MAP[obj.type] || 'saved-answer';
            let path = `/${urlSegment}/${obj.guid}/`;
            if (obj.type === EurekaObjectType.VISUALIZATION) {
                path = `/${urlSegment}/${obj.parentGuid}/${obj.guid}`;
            }
            else if (obj.type === EurekaObjectType.WORKSHEET) {
                path = `/${urlSegment}/${obj.guid}`;
            }
            return {
                id: `eureka-obj-${obj.guid}`,
                label: obj.name,
                description: obj.authorName
                    ? tStr('spotlight.eurekaObject.byAuthor', {
                        authorName: obj.authorName,
                    })
                    : undefined,
                icon: EUREKA_OBJECT_ICON_MAP[obj.type] || IconID.ANSWER,
                type: SpotlightItemType.EUREKA_OBJECT,
                subtype: obj.type,
                path,
            };
        });
    }, [isSearching, objectCompletions]);
    const eurekaQueryItems = useMemo(() => {
        if (!isSearching)
            return [];
        return queryCompletions.map(qc => ({
            id: `eureka-query-${qc.query}`,
            label: qc.query,
            description: tStr('spotlight.eurekaQuery.description'),
            icon: qc.type === EurekaQueryCompletionType.RECENT
                ? IconID.CLOCK
                : IconID.SEARCH_V2,
            type: SpotlightItemType.EUREKA_QUERY,
            path: `${eurekaSearchPath}?query=${encodeURIComponent(qc.query)}`,
        }));
    }, [isSearching, queryCompletions, eurekaSearchPath, tStr]);
    const eurekaFilterScopedItems = useMemo(() => {
        if (!searchQuery.trim())
            return filterScopedItems;
        const isLiveboardFilter = !!(activeFilter?.id.includes('liveboard') ||
            activeFilter?.id.includes('pinboard'));
        const isAnswerFilter = !!activeFilter?.id.includes('answer');
        const isModelFilter = !!activeFilter?.id.includes('model');
        if (isLiveboardFilter) {
            return eurekaObjectItems.filter(item => item.subtype === EurekaObjectType.PINBOARD);
        }
        if (isAnswerFilter) {
            return eurekaObjectItems.filter(item => item.subtype === EurekaObjectType.ANSWER ||
                item.subtype === EurekaObjectType.VISUALIZATION);
        }
        if (isModelFilter) {
            return eurekaObjectItems.filter(item => item.subtype === EurekaObjectType.WORKSHEET);
        }
        return filterScopedItems;
    }, [filterScopedItems, eurekaObjectItems, activeFilter, searchQuery]);
    const quickLinkIds = useMemo(() => new Set(quickLinks.map(ql => ql.id)), [
        quickLinks,
    ]);
    const allItems = useMemo(() => (customItems ?? []).filter(item => !quickLinkIds.has(item.id) &&
        (showSpotterItem || !item.keywords?.includes('spotter'))), [customItems, quickLinkIds, showSpotterItem]);
    const filteredItems = useMemo(() => filterSpotlightItems(allItems, searchQuery), [allItems, searchQuery]);
    const groupedItems = useMemo(() => {
        if (isSearching)
            return null;
        const navigationItems = filteredItems.filter(item => item.type === SpotlightItemType.NAVIGATION);
        const actionItems = filteredItems.filter(item => item.type === SpotlightItemType.CREATE);
        const groups = [];
        if (navigationItems.length > 0) {
            groups.push({
                id: 'navigation',
                title: 'Navigation',
                items: navigationItems,
            });
        }
        if (actionItems.length > 0) {
            groups.push({
                id: 'actions',
                title: 'Quick Actions',
                items: actionItems,
            });
        }
        return groups;
    }, [filteredItems, isSearching]);
    const displayItems = useMemo(() => {
        if (isSearching) {
            return [
                ...eurekaQueryItems,
                ...eurekaObjectItems,
                ...filteredItems,
            ];
        }
        if (!groupedItems)
            return filteredItems;
        return groupedItems.flatMap(group => group.items);
    }, [
        isSearching,
        filteredItems,
        groupedItems,
        eurekaQueryItems,
        eurekaObjectItems,
    ]);
    const orderedCreateActions = useMemo(() => {
        const actionItems = displayItems.filter(item => item.type === SpotlightItemType.CREATE &&
            (showSpotterItem || !item.keywords?.includes('spotter')));
        return CREATE_ACTION_IDS.map(id => actionItems.find(item => item.id === id)).filter(Boolean);
    }, [displayItems, showSpotterItem]);
    const visibleQuickLinks = useMemo(() => {
        const adminFiltered = showAdminItem
            ? quickLinks
            : quickLinks.filter(item => item.id !== 'quick-admin-settings' &&
                item.id !== 'quick-profile-settings');
        return isPathEnabled
            ? adminFiltered.filter(ql => !ql.path || isPathEnabled(ql.path))
            : adminFiltered;
    }, [showAdminItem, isPathEnabled, quickLinks]);
    const spotterSuggestionItem = useMemo(() => showSpotterItem
        ? buildSpotterSuggestionItem({
            isSearching,
            spotterPath,
            query: searchQuery,
            enableAutoMode: enableSpotterAutoMode,
            t: tStr,
        })
        : null, [
        showSpotterItem,
        isSearching,
        spotterPath,
        searchQuery,
        enableSpotterAutoMode,
        tStr,
    ]);
    const objectSearchItem = useMemo(() => buildObjectSearchItem({
        isSearching,
        eurekaSearchPath,
        query: searchQuery,
        t: tStr,
    }), [isSearching, eurekaSearchPath, searchQuery, tStr]);
    const navigableItems = useMemo(() => {
        if (isSlashMode)
            return slashItems;
        if (isFilterMode)
            return eurekaFilterScopedItems;
        if (searchQuery.trim()) {
            return [
                ...eurekaQueryItems,
                ...eurekaObjectItems,
                ...filteredItems,
                ...(objectSearchItem ? [objectSearchItem] : []),
                ...(spotterSuggestionItem ? [spotterSuggestionItem] : []),
            ];
        }
        if (displayItems.length === 0) {
            return [];
        }
        const recentDisplayed = hydratedRecentItems.slice(0, RECENT_CONFIG.displayCount);
        return [
            ...recentDisplayed,
            ...orderedCreateActions,
            ...visibleQuickLinks,
        ];
    }, [
        isSlashMode,
        slashItems,
        isFilterMode,
        eurekaFilterScopedItems,
        searchQuery,
        filteredItems,
        eurekaQueryItems,
        eurekaObjectItems,
        displayItems.length,
        hydratedRecentItems,
        orderedCreateActions,
        visibleQuickLinks,
        objectSearchItem,
        spotterSuggestionItem,
    ]);
    const openSpotlight = useCallback((method = 'click') => {
        isClosingRef.current = false;
        openedAtRef.current = Date.now();
        openedMethodRef.current = method;
        setIsOpen(true);
        setSearchQuery('');
        setFocusedIndex(0);
        let nextOnboardingIndex = 0;
        try {
            const stored = localStorage.getItem(onboardingLastShownKey);
            if (stored !== null) {
                const lastShown = parseInt(stored, 10);
                if (!Number.isNaN(lastShown)) {
                    nextOnboardingIndex =
                        (lastShown + 1) % visibleOnboardingHintKeys.length;
                }
            }
        }
        catch {
        }
        setOnboardingTabIndex(nextOnboardingIndex);
        setOnboardingTabVisible(true);
        try {
            collectEvent(new UserAction(userActionTypeEnum.COMMAND_PALETTE_OPEN), {
                navigationMethod: method === 'keyboard'
                    ? 'keyboard (cmd+k)'
                    : 'click on top nav',
            });
        }
        catch {
        }
    }, [onboardingLastShownKey, visibleOnboardingHintKeys]);
    const closeSpotlight = useCallback((restoreFocus = false) => {
        isClosingRef.current = true;
        setIsOpen(false);
        setSearchQuery('');
        setFocusedIndex(0);
        setActiveFilter(null);
        localStorage.setItem(onboardingLastShownKey, String(onboardingTabIndexRef.current));
        if (restoreFocus) {
            window.dispatchEvent(new CustomEvent('spotlight:close'));
        }
    }, [onboardingLastShownKey]);
    useSpotlightShortcut({
        onOpen: () => openSpotlight('keyboard'),
        onClose: () => closeSpotlight(true),
        isOpen,
        enabled,
    });
    useEffect(() => {
        const handler = () => openSpotlight('click');
        window.addEventListener('spotlight:open', handler);
        return () => window.removeEventListener('spotlight:open', handler);
    }, [openSpotlight]);
    useEffect(() => {
        if (!isOpen)
            return undefined;
        const handlePopState = () => {
            closeSpotlight();
        };
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isOpen, closeSpotlight]);
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isOpen]);
    useEffect(() => {
        const container = containerRef.current;
        if (!isOpen || !container)
            return undefined;
        const handleFocusOut = (e) => {
            if (isClosingRef.current)
                return;
            if (e.relatedTarget instanceof Node &&
                container.contains(e.relatedTarget)) {
                return;
            }
            searchInputRef.current?.focus();
        };
        container.addEventListener('focusout', handleFocusOut);
        return () => container.removeEventListener('focusout', handleFocusOut);
    }, [isOpen]);
    useEffect(() => {
        if (!isSlashMode)
            setFocusedIndex(0);
    }, [navigableItems.length]);
    useEffect(() => {
        if (listRef.current && focusedIndex >= 0) {
            const focusedElement = listRef.current.querySelector(`[data-index="${focusedIndex}"]`);
            if (focusedElement) {
                focusedElement.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth',
                });
            }
        }
    }, [focusedIndex]);
    useEffect(() => {
        if (!isSearching || !isOpen)
            return undefined;
        if (showResultsTimerRef.current) {
            clearTimeout(showResultsTimerRef.current);
        }
        showResultsTimerRef.current = setTimeout(() => {
            const counts = {};
            navigableItems.forEach(item => {
                const key = `${item.type}ResultsCount`;
                counts[key] = (counts[key] || 0) + 1;
            });
            try {
                collectEvent(new UserAction(userActionTypeEnum.COMMAND_PALETTE_SHOW_RESULTS), {
                    inputQueryLength: searchQuery.length,
                    totalResults: navigableItems.length,
                    topResultType: navigableItems[0]?.type ?? '',
                    queryText: searchQuery,
                    ...counts,
                });
            }
            catch {
            }
        }, 300);
        return () => {
            if (showResultsTimerRef.current) {
                clearTimeout(showResultsTimerRef.current);
            }
        };
    }, [isSearching, isOpen, searchQuery, navigableItems]);
    const handleSelectItem = useCallback((item, selectionMethod = 'click', position) => {
        if (item.type !== SpotlightItemType.OBJECT_SEARCH &&
            item.type !== SpotlightItemType.CREATE) {
            setRecentItems(prev => {
                const deduped = prev.filter(i => i.id !== item.id);
                return [item, ...deduped].slice(0, RECENT_CONFIG.maxItems);
            });
        }
        let section;
        if (item.type === SpotlightItemType.SPOTTER_SUGGESTION) {
            section = 'spotter';
        }
        else if (item.type === SpotlightItemType.OBJECT_SEARCH) {
            section = 'search results';
        }
        else if (!isSearching &&
            recentItems.some(r => r.id === item.id)) {
            section = 'recents';
        }
        else if (!isSearching &&
            visibleQuickLinks.some(q => q.id === item.id)) {
            section = 'quick links';
        }
        else {
            section = 'search results';
        }
        try {
            collectEvent(new UserAction(userActionTypeEnum.COMMAND_PALETTE_RESULT_SELECTED), {
                selectionType: item.type,
                ...(position !== undefined && {
                    resultPosition: position + 1,
                }),
                section,
                queryText: searchQuery,
                timeToSelect_ms: Date.now() - openedAtRef.current,
                selectionMethod,
            });
        }
        catch {
        }
        closeSpotlight();
        if (onItemSelect) {
            onItemSelect(item);
        }
        else if (item.path && navService?.goToUrl) {
            navService.goToUrl(item.path);
        }
        else if (item.action) {
            item.action();
        }
    }, [
        closeSpotlight,
        onItemSelect,
        navService,
        isSearching,
        recentItems,
        visibleQuickLinks,
        searchQuery,
    ]);
    const handleSlashItemClick = useCallback((item) => {
        setActiveFilter(item);
        setSearchQuery('');
        setFocusedIndex(0);
        onFilterSelect?.(item);
        setTimeout(() => searchInputRef.current?.focus(), 0);
    }, [onFilterSelect]);
    useEffect(() => {
        onboardingTabIndexRef.current = onboardingTabIndex;
    }, [onboardingTabIndex]);
    const handleOnboardingPrev = useCallback(() => {
        setOnboardingTabIndex(prev => prev > 0 ? prev - 1 : visibleOnboardingHintKeys.length - 1);
    }, [visibleOnboardingHintKeys]);
    const handleOnboardingNext = useCallback(() => {
        setOnboardingTabIndex(prev => prev < visibleOnboardingHintKeys.length - 1 ? prev + 1 : 0);
    }, [visibleOnboardingHintKeys]);
    const handleOnboardingClose = useCallback(() => {
        setOnboardingTabVisible(false);
        onOnboardingTabClose?.();
    }, [onOnboardingTabClose]);
    const renderHintText = (text) => {
        const TOKEN_LABELS = {
            '[slash]': '/',
            '[tab]': 'Tab',
        };
        const tokens = text.split(/(\[slash\]|\[tab\])/).map((token, id) => ({ token, id }));
        return (React.createElement(React.Fragment, null, tokens.map(({ token, id }) => {
            const label = TOKEN_LABELS[token];
            if (label !== undefined) {
                return (React.createElement("kbd", { key: `token-${id}`, className: styles.slashKeyIcon }, label));
            }
            return token;
        })));
    };
    const clearFilter = useCallback(() => {
        setActiveFilter(null);
        setSearchQuery('');
        setFocusedIndex(0);
        searchInputRef.current?.focus();
    }, []);
    const handleKeyDown = useCallback((event) => {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                isKeyboardNavigating.current = true;
                setFocusedIndex(prev => prev < navigableItems.length - 1 ? prev + 1 : 0);
                break;
            case 'ArrowUp':
                event.preventDefault();
                isKeyboardNavigating.current = true;
                setFocusedIndex(prev => prev > 0 ? prev - 1 : navigableItems.length - 1);
                break;
            case 'Enter':
                event.preventDefault();
                if (event.metaKey &&
                    !isSlashMode &&
                    navigableItems.length > 0 &&
                    focusedIndex < navigableItems.length) {
                    const item = navigableItems[focusedIndex];
                    if (item.path) {
                        const url = `${window.location.origin}/#${item.path}`;
                        const a = document.createElement('a');
                        a.href = url;
                        a.target = '_blank';
                        a.rel = 'noopener';
                        a.click();
                    }
                }
                else if (focusedIndex >= 0 &&
                    focusedIndex < navigableItems.length) {
                    if (isSlashMode) {
                        handleSlashItemClick(navigableItems[focusedIndex]);
                    }
                    else {
                        handleSelectItem(navigableItems[focusedIndex], 'keyboard', focusedIndex);
                    }
                }
                break;
            case 'Backspace':
                if (activeFilter && !searchQuery) {
                    clearFilter();
                }
                break;
            case 'Tab':
                event.preventDefault();
                if (!searchQuery && !activeFilter && showSpotterItem) {
                    const spotterSlashItem = getSlashMenuItems(t).find(item => item.id === 'slash-spotter');
                    if (spotterSlashItem) {
                        handleSlashItemClick(spotterSlashItem);
                    }
                }
                break;
            case 'ArrowLeft':
            case 'ArrowRight':
                if ((event.altKey ||
                    event.metaKey ||
                    event.ctrlKey) &&
                    !searchQuery) {
                    event.preventDefault();
                }
                break;
            case '[':
            case ']':
                if (event.metaKey) {
                    event.preventDefault();
                }
                break;
            case 'Escape':
                event.preventDefault();
                if (activeFilter) {
                    clearFilter();
                }
                else {
                    closeSpotlight(true);
                }
                break;
            default:
                break;
        }
    }, [
        navigableItems,
        focusedIndex,
        isSlashMode,
        activeFilter,
        handleSlashItemClick,
        handleSelectItem,
        clearFilter,
        closeSpotlight,
        spotterPath,
        searchQuery,
        navService,
        showSpotterItem,
        t,
    ]);
    const handleOverlayClick = useCallback((event) => {
        if (event.target === event.currentTarget) {
            closeSpotlight();
        }
    }, [closeSpotlight]);
    const getTypeLabel = (item) => {
        const kws = (item.keywords || []).map(k => k.toLowerCase());
        const path = item.path || '';
        const hasKeyword = (list) => kws.some(k => list.includes(k));
        switch (item.type) {
            case SpotlightItemType.EUREKA_OBJECT:
                if (item.subtype === EurekaObjectType.VISUALIZATION) {
                    return tStr('spotlight.typeLabel.answerInLiveboard');
                }
                if (item.subtype === EurekaObjectType.PINBOARD) {
                    return tStr('PINBOARD');
                }
                if (item.subtype === EurekaObjectType.WORKSHEET) {
                    return tStr('spotlight.typeLabel.model');
                }
                return tStr('ANSWER');
            case SpotlightItemType.EUREKA_QUERY:
                return tStr('spotlight.typeLabel.search');
            case SpotlightItemType.SPOTTER_SUGGESTION:
                return tStr('gnav.appSwitcher.SPOTTER');
            case SpotlightItemType.OBJECT_SEARCH:
                return tStr('spotlight.typeLabel.objectSearch');
            case SpotlightItemType.CREATE:
                if (hasKeyword(TYPE_LABEL_CONFIG.spotter.keywords)) {
                    return tStr('gnav.appSwitcher.SPOTTER');
                }
                return tStr('CREATE');
            case SpotlightItemType.NAVIGATION:
            default:
                if (item.id.startsWith('data-') ||
                    item.id.startsWith('insights-') ||
                    item.id.startsWith('develop-')) {
                    return tStr('spotlight.filter.navigate');
                }
                if (hasKeyword(TYPE_LABEL_CONFIG.spotter.keywords) ||
                    path.includes('conv-assist')) {
                    return tStr('gnav.appSwitcher.SPOTTER');
                }
                if (hasKeyword(TYPE_LABEL_CONFIG.adminSettings.keywords)) {
                    return tStr('sidebar.header.newNav.title');
                }
                if (hasKeyword(TYPE_LABEL_CONFIG.help.keywords)) {
                    return tStr('homeleftpanel.help');
                }
                if (kws.includes('model'))
                    return tStr('spotlight.typeLabel.model');
                if (hasKeyword(TYPE_LABEL_CONFIG.liveboard.keywords)) {
                    return tStr('PINBOARD');
                }
                if (hasKeyword(TYPE_LABEL_CONFIG.answer.keywords)) {
                    return tStr('ANSWER');
                }
                if (hasKeyword(TYPE_LABEL_CONFIG.table.keywords)) {
                    return tStr('TABLES');
                }
                return tStr('spotlight.filter.navigate');
        }
    };
    const highlightText = (text, query) => {
        const q = query.trim();
        if (!q)
            return text;
        const idx = text.toLowerCase().indexOf(q.toLowerCase());
        if (idx === -1)
            return text;
        return (React.createElement(React.Fragment, null,
            text.slice(0, idx),
            React.createElement("strong", null, text.slice(idx, idx + q.length)),
            text.slice(idx + q.length)));
    };
    const getDisplayIcon = (item) => {
        const kws = (item.keywords || []).map(k => k.toLowerCase());
        const hasKeyword = (list) => kws.some(k => list.includes(k));
        switch (item.type) {
            case SpotlightItemType.ADMIN:
                return IconID.COG;
            case SpotlightItemType.NAVIGATION:
                if (item.id.startsWith('insights-') ||
                    item.id.startsWith('data-') ||
                    item.id.startsWith('develop-')) {
                    return IconID.PAPER_PLANE;
                }
                if (hasKeyword(TYPE_LABEL_CONFIG.spotter.keywords) ||
                    item.path?.includes('conv-assist')) {
                    return IconID.SPOTTER;
                }
                if (hasKeyword(TYPE_LABEL_CONFIG.adminSettings.keywords)) {
                    return IconID.COG;
                }
                if (hasKeyword(TYPE_LABEL_CONFIG.help.keywords)) {
                    return item.icon || IconID.PAPER_PLANE;
                }
                if (hasKeyword(TYPE_LABEL_CONFIG.table.keywords)) {
                    return IconID.CHART_TABLE;
                }
                if (kws.includes('liveboards') ||
                    kws.includes('pinboards') ||
                    hasKeyword(TYPE_LABEL_CONFIG.answer.keywords) ||
                    kws.includes('model')) {
                    return item.icon || IconID.PAPER_PLANE;
                }
                return IconID.PAPER_PLANE;
            case SpotlightItemType.SPOTTER_SUGGESTION:
                return IconID.SPOTTER;
            case SpotlightItemType.OBJECT_SEARCH:
                return IconID.MAGNIFYING_GLASS;
            default:
                return item.icon || IconID.SEARCH_V2;
        }
    };
    const getItemLabel = (item) => {
        if (item.labelNode)
            return item.labelNode;
        if (item.type === SpotlightItemType.SPOTTER_SUGGESTION) {
            const prefix = tStr('spotlight.completion.openInSpotter').replace('{query}', '');
            const query = item.label.startsWith(prefix)
                ? item.label.slice(prefix.length)
                : item.label;
            return (React.createElement(React.Fragment, null,
                prefix,
                React.createElement("strong", null, query)));
        }
        return item.label;
    };
    const renderListItem = (item, index, keyPrefix = '', isRecent = false) => {
        const isFocused = index === focusedIndex;
        return (React.createElement("button", { key: `${keyPrefix}${item.id}`, type: "button", className: `${styles.listItem} ${isFocused ? styles.focused : ''}`, onClick: () => handleSelectItem(item, 'click', index), onMouseEnter: () => {
                if (!isKeyboardNavigating.current) {
                    setFocusedIndex(index);
                }
            }, "data-index": index, tabIndex: -1 },
            item.type === SpotlightItemType.CREATE &&
                (!item.icon || item.icon === IconID.PLUS) ? (React.createElement(Horizontal, { vAlignContent: "center", hAlignContent: "center", className: styles.itemIcon },
                React.createElement("div", { className: styles.itemIconButton },
                    React.createElement(Icon, { id: IconID.PLUS, size: IconSize.XSMALL, color: IconColor.TEXT_COLOR })))) : (React.createElement(Horizontal, { vAlignContent: "center", className: styles.itemIcon },
                React.createElement(Icon, { id: isRecent ? IconID.CLOCK : getDisplayIcon(item), size: IconSize.MEDIUM, color: IconColor.GRAY }))),
            React.createElement(Horizontal, { grow: true, vAlignContent: "center", className: styles.itemContent },
                React.createElement(Typography, { variant: "body-normal", noMargin: true, className: cx(styles.itemLabel, {
                        [styles.itemLabelFull]: !item.description,
                    }) }, (isFilterMode || isSearching) && searchQuery.trim()
                    ? highlightText(item.label, searchQuery)
                    : getItemLabel(item)),
                item.description && (React.createElement(Typography, { variant: "p-small", noMargin: true, className: styles.itemDescription }, item.description))),
            React.createElement(Typography, { variant: "caption", noMargin: true, className: styles.itemType }, getTypeLabel(item))));
    };
    const renderResults = () => {
        if (isSlashMode) {
            if (slashItems.length === 0) {
                return (React.createElement(Vertical, { hAlignContent: "center", className: styles.emptyState },
                    React.createElement(Typography, { variant: "body-normal", noMargin: true, className: styles.emptyTitle }, tStr('spotlight.noResults'))));
            }
            return (React.createElement(React.Fragment, null,
                React.createElement(Typography, { key: "slash-header", variant: "section-label", noMargin: true, className: styles.categoryTitle }, tStr('spotlight.slashMenu.header')),
                slashItems.map((item, idx) => {
                    const isFocused = idx === focusedIndex;
                    return (React.createElement("button", { key: item.id, type: "button", className: `${styles.listItem} ${isFocused ? styles.focused : ''}`, onClick: () => handleSlashItemClick(item), onMouseEnter: () => {
                            if (!isKeyboardNavigating.current) {
                                setFocusedIndex(idx);
                            }
                        }, "data-index": idx, tabIndex: -1 },
                        React.createElement(Horizontal, { vAlignContent: "center", hAlignContent: "center", className: styles.itemIcon }, item.id === 'slash-create' ? (React.createElement("div", { className: styles.itemIconButton },
                            React.createElement(Icon, { id: item.icon || IconID.PLUS, size: IconSize.XSMALL, color: IconColor.TEXT_COLOR }))) : (React.createElement(Icon, { id: item.icon || IconID.SEARCH_V2, size: IconSize.MEDIUM }))),
                        React.createElement(Vertical, { className: styles.itemContent },
                            React.createElement(Typography, { variant: "body-normal", noMargin: true, className: `${styles.itemLabel} ${styles.itemLabelFull}` }, item.label)),
                        React.createElement("span", { className: styles.itemType }, "Filter")));
                })));
        }
        if (isFilterMode) {
            const isEurekaFilter = !!(activeFilter?.id.includes('liveboard') ||
                activeFilter?.id.includes('pinboard') ||
                activeFilter?.id.includes('answer') ||
                activeFilter?.id.includes('model'));
            const isLoading = eurekaFilterScopedItems.length === 0 &&
                (filterItemsLoading ||
                    (isEurekaFilter && !!searchQuery.trim() && eurekaLoading));
            if (isLoading) {
                return (React.createElement(Horizontal, { vAlignContent: "center", className: styles.eurekaLoadingRow },
                    React.createElement(LoadingIndicator.Contextual, { className: styles.eurekaLoadingSpinner })));
            }
            if (eurekaFilterScopedItems.length === 0) {
                const isSpotterFilter = activeFilter?.id === 'slash-spotter';
                return (React.createElement(Vertical, { hAlignContent: "center", className: styles.emptyState },
                    React.createElement(Typography, { variant: "body-normal", noMargin: true, className: styles.emptyTitle }, isSpotterFilter && !searchQuery.trim()
                        ? tStr('spotlight.filter.spotter.hint')
                        : tStr('spotlight.noResults')),
                    (!isSpotterFilter || !!searchQuery.trim()) && (React.createElement(Typography, { variant: "p-small", noMargin: true, className: styles.emptyDescription }, searchQuery.trim()
                        ? tStr('spotlight.noResultsSuggestion')
                        : tStr('spotlight.filter.noItems', {
                            filter: activeFilter?.label ?? '',
                        })))));
            }
            return (React.createElement(React.Fragment, null,
                activeFilter?.id === 'slash-spotter' &&
                    !searchQuery.trim() && (React.createElement(Typography, { key: "spotter-recent-header", variant: "section-label", noMargin: true, className: styles.categoryTitle }, tStr('spotlight.section.recent'))),
                eurekaFilterScopedItems.map((item, idx) => renderListItem(item, idx))));
        }
        if (isSearching) {
            const hasEurekaResults = eurekaQueryItems.length > 0 || eurekaObjectItems.length > 0;
            const hasAppResults = filteredItems.length > 0;
            if (eurekaLoading && !hasEurekaResults && !hasAppResults) {
                return (React.createElement(React.Fragment, null,
                    spotterSuggestionItem &&
                        renderListItem(spotterSuggestionItem, 0),
                    React.createElement(Horizontal, { vAlignContent: "center", hAlignContent: "center", className: styles.eurekaLoadingRow },
                        React.createElement(LoadingIndicator.Contextual, { className: styles.eurekaLoadingSpinner }))));
            }
            if (!hasEurekaResults && !hasAppResults && !eurekaLoading) {
                if (objectSearchItem || spotterSuggestionItem) {
                    const objectSearchIndex = 0;
                    const spotterIndex = objectSearchItem ? 1 : 0;
                    return (React.createElement(React.Fragment, null,
                        objectSearchItem &&
                            renderListItem(objectSearchItem, objectSearchIndex),
                        spotterSuggestionItem &&
                            renderListItem(spotterSuggestionItem, spotterIndex)));
                }
                return (React.createElement(Vertical, { hAlignContent: "center", className: styles.emptyState },
                    React.createElement(Typography, { variant: "body-normal", noMargin: true, className: styles.emptyTitle }, "No results found"),
                    React.createElement(Typography, { variant: "p-small", noMargin: true, className: styles.emptyDescription }, "Try a different search term")));
            }
            const results = [];
            let itemIndex = 0;
            eurekaQueryItems.forEach(item => {
                results.push(renderListItem(item, itemIndex));
                itemIndex += 1;
            });
            eurekaObjectItems.forEach(item => {
                results.push(renderListItem(item, itemIndex));
                itemIndex += 1;
            });
            filteredItems.forEach(item => {
                results.push(renderListItem(item, itemIndex));
                itemIndex += 1;
            });
            if (objectSearchItem) {
                results.push(renderListItem(objectSearchItem, itemIndex));
                itemIndex += 1;
            }
            if (spotterSuggestionItem) {
                results.push(renderListItem(spotterSuggestionItem, itemIndex));
            }
            if (eurekaLoading) {
                results.push(React.createElement(Horizontal, { key: "search-loading", vAlignContent: "center", hAlignContent: "center", className: styles.eurekaLoadingRow },
                    React.createElement(LoadingIndicator.Contextual, { className: styles.eurekaLoadingSpinner })));
            }
            return results;
        }
        if (displayItems.length === 0) {
            return (React.createElement(Vertical, { hAlignContent: "center", className: styles.emptyState },
                React.createElement(Icon, { id: IconID.SEARCH_V2, size: IconSize.LARGE, className: styles.emptyIcon }),
                React.createElement(Typography, { variant: "body-normal", noMargin: true, className: styles.emptyTitle }, tStr('spotlight.noResults')),
                React.createElement(Typography, { variant: "p-small", noMargin: true, className: styles.emptyDescription }, tStr('spotlight.noResultsSuggestion'))));
        }
        const results = [];
        if (hydratedRecentItems.length > 0) {
            results.push(React.createElement(Typography, { key: "recent-header", variant: "section-label", noMargin: true, className: styles.categoryTitle }, tStr('spotlight.section.recent')));
            hydratedRecentItems
                .slice(0, RECENT_CONFIG.displayCount)
                .forEach((item, idx) => {
                results.push(renderListItem(item, idx, 'recent-', true));
            });
        }
        if (orderedCreateActions.length > 0) {
            const createOffset = Math.min(hydratedRecentItems.length, RECENT_CONFIG.displayCount);
            results.push(React.createElement(Typography, { key: "actions-header", variant: "section-label", noMargin: true, className: styles.categoryTitle }, tStr('CREATE')));
            orderedCreateActions.forEach((item, idx) => {
                results.push(renderListItem(item, createOffset + idx, 'create-'));
            });
        }
        if (visibleQuickLinks.length > 0) {
            const quickLinkOffset = Math.min(hydratedRecentItems.length, RECENT_CONFIG.displayCount) +
                orderedCreateActions.length;
            results.push(React.createElement(Typography, { key: "nav-header", variant: "section-label", noMargin: true, className: styles.categoryTitle }, tStr('spotlight.section.quickLinks')));
            visibleQuickLinks.forEach((item, idx) => {
                results.push(renderListItem(item, quickLinkOffset + idx));
            });
        }
        return results;
    };
    if (!isOpen) {
        return null;
    }
    return ReactDOM.createPortal(React.createElement("div", { ref: containerRef, className: styles.spotlightOverlay, onClick: handleOverlayClick, onKeyDown: handleKeyDown, role: "dialog", "aria-modal": "true", "aria-label": tStr('spotlight.ariaLabel') },
        React.createElement(Vertical, { className: styles.spotlightContainer },
            React.createElement("div", { className: styles.searchSection },
                React.createElement(Horizontal, { vAlignContent: "center", className: styles.searchInputWrapper },
                    React.createElement(Icon, { id: IconID.MAGNIFYING_GLASS, size: IconSize.SMALL, className: styles.searchIcon }),
                    activeFilter && (React.createElement("div", { className: styles.filterChip },
                        React.createElement("span", { className: styles.filterChipLabel }, activeFilter.label),
                        React.createElement("button", { type: "button", className: styles.filterChipClose, onClick: clearFilter, "aria-label": tStr('spotlight.clearFilter') },
                            React.createElement(Icon, { id: IconID.CROSS, size: IconSize.SMALL, color: IconColor.TEXT_COLOR })))),
                    React.createElement("input", { ref: searchInputRef, type: "text", className: styles.searchInput, placeholder: activeFilter
                            ? ''
                            : tStr('spotlight.searchPlaceholder'), value: searchQuery, onChange: e => {
                            const val = e.target.value;
                            if ((val.startsWith('/') ||
                                val.startsWith('@')) &&
                                activeFilter) {
                                setActiveFilter(null);
                            }
                            setSearchQuery(val);
                        }, autoComplete: "off", autoCorrect: "off", autoCapitalize: "off", spellCheck: false }),
                    eurekaLoading && (React.createElement("div", { className: styles.searchingIndicator, "aria-label": tStr('spotlight.searching') })),
                    !eurekaLoading &&
                        !activeFilter &&
                        (searchQuery.trim() && !isSlashMode ? (React.createElement("button", { type: "button", className: styles.clearButton, "aria-label": tStr('spotlight.clearSearch'), onClick: () => {
                                setSearchQuery('');
                                searchInputRef.current?.focus();
                            } }, "\u2715")) : (React.createElement(Horizontal, { className: styles.shortcutBadge },
                            React.createElement("kbd", { className: styles.slashKeyIcon }, "/"),
                            React.createElement("span", null, tStr('spotlight.slashHint'))))))),
            React.createElement("div", { ref: listRef, className: styles.resultsSection, onMouseMove: () => {
                    isKeyboardNavigating.current = false;
                } },
                !isSearching &&
                    !isSlashMode &&
                    !isFilterMode &&
                    onboardingTabVisible && (React.createElement("div", { className: styles.onboardingTab },
                    React.createElement("span", { className: styles.onboardingTabText }, renderHintText(tStr(visibleOnboardingHintKeys[onboardingTabIndex]))),
                    React.createElement("div", { className: styles.onboardingTabNavZone },
                        React.createElement("button", { type: "button", className: styles.onboardingTabNavBtn, onClick: handleOnboardingPrev, "aria-label": "Previous hint" },
                            React.createElement("span", { className: styles.onboardingTabNavCircle },
                                React.createElement(Icon, { id: IconID.CHEVRON_LEFT, size: IconSize.SMALL }))),
                        React.createElement("button", { type: "button", className: styles.onboardingTabNavBtn, onClick: handleOnboardingNext, "aria-label": "Next hint" },
                            React.createElement("span", { className: styles.onboardingTabNavCircle },
                                React.createElement(Icon, { id: IconID.CHEVRON_RIGHT, size: IconSize.SMALL }))),
                        React.createElement("button", { type: "button", className: styles.onboardingTabCloseBtn, onClick: handleOnboardingClose, "aria-label": "Dismiss" },
                            React.createElement(Icon, { id: IconID.CROSS, size: IconSize.SMALL }))))),
                renderResults()),
            React.createElement(Horizontal, { vAlignContent: "center", className: styles.footer },
                React.createElement(Horizontal, { vAlignContent: "center", className: styles.shortcutHintsLeft },
                    React.createElement(Horizontal, { vAlignContent: "center", className: styles.shortcutHint },
                        tStr('spotlight.shortcut.navigate'),
                        React.createElement("span", { className: styles.shortcutKey }, "\u2191\u2193")),
                    showSpotterItem &&
                        !isSlashMode &&
                        !activeFilter && (React.createElement(Horizontal, { vAlignContent: "center", className: styles.shortcutHint },
                        "Spotter",
                        React.createElement("span", { className: `${styles.shortcutKey} ${styles.shortcutKeyTab}` }, "Tab")))),
                React.createElement(Horizontal, { vAlignContent: "center", className: styles.shortcutHints },
                    !isSlashMode && (React.createElement(Horizontal, { vAlignContent: "center", className: styles.shortcutHint },
                        tStr('spotlight.shortcut.openNewTab'),
                        React.createElement("span", { className: styles.shortcutKey }, "\u2318"),
                        React.createElement("span", { className: styles.shortcutKey }, "+"),
                        React.createElement("span", { className: styles.shortcutKey }, "\u21B5"))),
                    React.createElement(Horizontal, { vAlignContent: "center", className: styles.shortcutHint },
                        tStr('spotlight.shortcut.open'),
                        React.createElement("span", { className: styles.shortcutKey }, "\u21B5")))))), document.body);
};
export default Spotlight;
//# sourceMappingURL=spotlight.js.map